import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import tesoreriaService from '../../../../services/tesoreriaService';
import { getApiErrorMessage } from '../../../../utils/apiError';
import { formatCurrency } from '../../../../utils/formatters';
import { isoFechaDesde12Meses } from '../../../../types/bcraCentralDeudores';
import type { Info360ChequeDevueltoItem } from '../types';
import {
  ChipEstadoChequeDevuelto,
  formatInfo360ChequeFecha,
  textoMotivoSeguimiento,
  type ChequeDevueltoEstadoInput,
} from '../chequesDevueltosDisplay';
import ChequeTerceroDetalleDialog, {
  type ChequeTerceroDetalleData,
} from '../../../../components/Tesoreria/ChequeTerceroDetalleDialog';
import type { ChequeTercero } from '../../../tesoreria/chequesTercero/types';

interface Props {
  open: boolean;
  onClose: () => void;
  nroCliente: number;
  resumenItems: Info360ChequeDevueltoItem[];
}

type ChequeListItem = Info360ChequeDevueltoItem | ChequeTercero;

const mapItemToDetalle = (item: ChequeListItem): ChequeTerceroDetalleData => ({
  nroCarga: item.nroCarga,
  nroBanco: item.nroBanco,
  nombreBanco: item.nombreBanco ?? undefined,
  nroSucursal: item.nroSucursal,
  nroCheque: item.nroCheque,
  nroLibrador: item.nroLibrador,
  librador: item.librador,
  importe: item.importe,
  fechaIngreso: item.fechaIngreso ?? '',
  nroMovCajaIngre: item.nroMovCajaIngre ?? null,
  nroDestino: 'nroDestino' in item ? item.nroDestino ?? null : null,
  fechaSalida: 'fechaSalida' in item ? item.fechaSalida ?? null : null,
  nroMovCajaSalida: 'nroMovCajaSalida' in item ? item.nroMovCajaSalida ?? null : null,
  fechaVencimiento: item.fechaVencimiento ?? '',
  concepto: item.concepto ?? '',
  fechaEmision: item.fechaEmision ?? '',
  conciliado: 'conciliado' in item ? item.conciliado ?? 0 : 0,
  asentado: 'asentado' in item ? item.asentado ?? 0 : 0,
  mcp: 'mcp' in item ? item.mcp ?? '' : '',
  nroCuenta: 'nroCuenta' in item ? item.nroCuenta ?? '' : '',
  rechazado: 'rechazado' in item && item.rechazado != null ? item.rechazado : 1,
  fechaRechazo: item.fechaRechazo ?? null,
});

const estadoInputFromItem = (item: ChequeListItem): ChequeDevueltoEstadoInput => ({
  concepto: item.concepto,
  rechazado: 'rechazado' in item && item.rechazado != null ? item.rechazado : 1,
  senial: 'senial' in item ? item.senial : undefined,
  conciliado: 'conciliado' in item ? item.conciliado : undefined,
  fechaSalida: 'fechaSalida' in item ? item.fechaSalida : undefined,
  nroMovCajaSalida: 'nroMovCajaSalida' in item ? item.nroMovCajaSalida : undefined,
  comprobanteSalida: 'comprobanteSalida' in item ? item.comprobanteSalida : undefined,
});

const Info360ChequesDevueltosDialog: React.FC<Props> = ({
  open,
  onClose,
  nroCliente,
  resumenItems,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ChequeListItem[]>([]);
  const [chequeDetalle, setChequeDetalle] = useState<ChequeTerceroDetalleData | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const resultado = await tesoreriaService.getChequesTerceros({
        estado: 'rechazado',
        nroCliente,
        fechaRechazoDesde: isoFechaDesde12Meses(),
        pageNumber: 1,
        pageSize: 1000,
      });
      setItems(resultado.items.length > 0 ? resultado.items : resumenItems);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cargar el detalle de cheques devueltos.'), {
        variant: 'error',
      });
      setItems(resumenItems);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, nroCliente, resumenItems]);

  useEffect(() => {
    if (open) void cargar();
  }, [open, cargar]);

  const handleClose = () => {
    setChequeDetalle(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Cheques devueltos — últimos 12 meses</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay cheques devueltos en el período.
            </Typography>
          ) : isMobile ? (
            <StackMobile items={items} onSelect={setChequeDetalle} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha rechazo</TableCell>
                    <TableCell>Nº cheque</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell>Motivo / seguimiento</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Banco</TableCell>
                    <TableCell>Librador</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.nroCarga}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setChequeDetalle(mapItemToDetalle(item))}
                    >
                      <TableCell>{formatInfo360ChequeFecha(item.fechaRechazo)}</TableCell>
                      <TableCell>{item.nroCheque}</TableCell>
                      <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                      <TableCell>{textoMotivoSeguimiento(item.concepto)}</TableCell>
                      <TableCell>
                        <ChipEstadoChequeDevuelto item={estadoInputFromItem(item)} />
                      </TableCell>
                      <TableCell>{item.nombreBanco || item.nroBanco}</TableCell>
                      <TableCell>{item.librador || item.nroLibrador}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ChequeTerceroDetalleDialog
        open={Boolean(chequeDetalle)}
        onClose={() => setChequeDetalle(null)}
        cheque={chequeDetalle}
      />
    </>
  );
};

const StackMobile: React.FC<{
  items: ChequeListItem[];
  onSelect: (data: ChequeTerceroDetalleData) => void;
}> = ({ items, onSelect }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((item) => (
      <Paper
        key={item.nroCarga}
        variant="outlined"
        sx={{ p: 1.5, cursor: 'pointer' }}
        onClick={() => onSelect(mapItemToDetalle(item))}
      >
        <Typography variant="body2" fontWeight={600}>
          Cheque {item.nroCheque} · {formatCurrency(item.importe)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {formatInfo360ChequeFecha(item.fechaRechazo)}
          {' · '}
          {item.nombreBanco || item.nroBanco}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Motivo: {textoMotivoSeguimiento(item.concepto)}
        </Typography>
        <Box sx={{ mt: 0.75 }}>
          <ChipEstadoChequeDevuelto item={estadoInputFromItem(item)} />
        </Box>
      </Paper>
    ))}
  </Box>
);

export default Info360ChequesDevueltosDialog;
